import { Class } from "../../../common/types/class";
import { Counterparty } from "../../../common/types/counterparty";
import { Account } from "../../../common/types/domains/accounting/account";
import { Entity } from "../../../common/types/entity";
import { FiltersTypes } from "../../../common/types/filters/general";
import { FinancialAccount } from "../../../common/types/financialAccount";
import {
    RuleActionType,
    RuleConditionAmount,
    RuleConditionType,
    TransactionRule,
    TransactionRuleCondition,
    TransactionRuleGroup,
} from "../../../common/types/transactionRule";

interface BuilRulesItemsProps {
    search: string | undefined;
    categoryLabelGetter: (id: string) => string;
    ruleGroups?: TransactionRuleGroup[];
    entities: Entity[];
    financialAccounts: FinancialAccount[];
    accounts: Account[];
    counterparties: Counterparty[];
    classesMap: Map<string, Class>;
    filters: Pick<FiltersTypes, "entityIds" | "rulesEnabledOnly">;
    activeTab: string;
}

export function buildRulesItems({
    search,
    categoryLabelGetter,
    ruleGroups,
    entities,
    financialAccounts,
    counterparties,
    filters,
    accounts,
    classesMap,
    activeTab,
}: BuilRulesItemsProps) {
    if (!ruleGroups || (!search && Object.keys(filters).length === 0)) {
        return ruleGroups;
    }

    const filteredRuleGroups = ruleGroups
        .filter(
            ({ isAccounting }) => isAccounting === (activeTab === "accounting"),
        )
        .map((ruleGroup) => {
            const filteredRules = (ruleGroup.rules ?? []).filter((rule) => {
                const conditionMatch = search
                    ? searchConditions({
                          conditions: rule.conditions,
                          search,
                          entities,
                          financialAccounts,
                          counterparties,
                          categoryLabelGetter,
                      })
                    : true;

                const conditionsFilter = filterConditions(
                    rule.conditions,
                    filters,
                );

                const actionMatch = search
                    ? searchActions({
                          rule,
                          search,
                          accounts,
                          entities,
                          counterparties,
                          categoryLabelGetter,
                          classesMap,
                      })
                    : true;

                const statusMatch =
                    !!filters.rulesEnabledOnly !== true || !rule.isDisabled;

                return (
                    (conditionMatch || actionMatch) &&
                    conditionsFilter &&
                    statusMatch
                );
            });

            return {
                ...ruleGroup,
                rules: filteredRules,
            };
        })
        .filter((group) => group !== undefined);

    return filteredRuleGroups;
}

function searchActions({
    rule,
    search,
    accounts,
    entities,
    counterparties,
    categoryLabelGetter,
    classesMap,
}: {
    rule: TransactionRule;
    search: string;
    accounts: Account[];
    entities?: Entity[];
    counterparties?: Counterparty[];
    categoryLabelGetter?: (id: string) => string;
    classesMap: Map<string, Class>;
}) {
    return rule.actions.some((action) => {
        switch (action.actionType) {
            case RuleActionType.ACCOUNT_ID:
                return searchAccountAction({
                    search,
                    accounts,
                    accountId: action.value as string,
                });
            case RuleActionType.CLASS:
                return searchClassAction({
                    search,
                    classesMap,
                    classIds: action.value as string[],
                });
            case RuleActionType.ACCOUNT_NAME:
                return searchAccountByNameAction({
                    search,
                    name: action.value as string,
                });
            case RuleActionType.CATEGORY:
                return categoryLabelGetter
                    ? searchCategoryAction({
                          search,
                          categoryId: action.value as string,
                          categoryLabelGetter,
                      })
                    : false;
            case RuleActionType.ENTITY:
                return entities
                    ? searchEntityAction({
                          search,
                          entityId: action.value as number,
                          entities,
                      })
                    : false;
            case RuleActionType.MEMO:
                return searchMemoAction(search, action.value as string);
            case RuleActionType.COUNTERPARTY:
                return counterparties
                    ? searchCounterpartyAction({
                          search,
                          counterparties,
                          counterpartyId: action.value as string,
                      })
                    : false;
            default:
                return false;
        }
    });
}

function searchAccountAction({
    search,
    accounts,
    accountId,
}: {
    search: string;
    accounts: Account[];
    accountId: string;
}) {
    return containsTextCaseInsensitive(
        findEntityById(accounts, accountId)?.name,
        search,
    );
}

function searchAccountByNameAction({
    search,
    name,
}: {
    search: string;
    name: string;
}) {
    return containsTextCaseInsensitive(name, search);
}

function searchClassAction({
    search,
    classesMap,
    classIds,
}: {
    search: string;
    classesMap: Map<string, Class>;
    classIds: string[];
}) {
    return classIds.some((classId) =>
        containsTextCaseInsensitive(classesMap.get(classId)?.label, search),
    );
}

// Reuse searchCategoryCondition for category actions
function searchCategoryAction({
    search,
    categoryId,
    categoryLabelGetter,
}: {
    search: string;
    categoryId: string;
    categoryLabelGetter: (id: string) => string;
}) {
    return searchCategoryCondition({ search, categoryId, categoryLabelGetter });
}

function searchEntityAction({
    search,
    entityId,
    entities,
}: {
    search: string;
    entityId: number;
    entities: Entity[];
}) {
    return containsTextCaseInsensitive(
        findEntityById(entities, entityId)?.name,
        search,
    );
}

function searchMemoAction(search: string, memo?: string) {
    if (!memo) {
        return false;
    }
    return containsTextCaseInsensitive(memo, search);
}

// Reuse searchCounterpartyCondition for counterparty actions
function searchCounterpartyAction({
    search,
    counterparties,
    counterpartyId,
}: {
    search: string;
    counterparties: Counterparty[];
    counterpartyId: string;
}) {
    return searchCounterpartyCondition({
        search,
        counterparties,
        counterpartyId,
    });
}

const filterConditions = (
    conditions: TransactionRuleCondition[],
    filters: Pick<FiltersTypes, "entityIds">,
) => {
    const entityFilters = filters.entityIds;
    if (entityFilters?.length) {
        return conditions.every(
            (condition) =>
                condition.conditionType !== RuleConditionType.ENTITY ||
                (condition.value as number[]).some((id) =>
                    entityFilters.includes(id),
                ),
        );
    }

    return true;
};
function searchConditions({
    conditions,
    search,
    entities,
    financialAccounts,
    counterparties,
    categoryLabelGetter,
}: {
    conditions: TransactionRuleCondition[];
    search: string;
    entities: Entity[];
    financialAccounts: FinancialAccount[];
    counterparties: Counterparty[];
    categoryLabelGetter: (id: string) => string;
}) {
    return conditions.some((condition) => {
        switch (condition.conditionType) {
            case RuleConditionType.COUNTERPARTY:
                return searchCounterpartyCondition({
                    search,
                    counterparties,
                    counterpartyId: condition.value as string,
                });
            case RuleConditionType.CATEGORY:
            case RuleConditionType.SUGGESTED_CATEGORY:
                return searchCategoryCondition({
                    search,
                    categoryId: condition.value as string,
                    categoryLabelGetter,
                });
            case RuleConditionType.DESCRIPTION_IS:
            case RuleConditionType.DESCRIPTION_INCLUDES:
                return searchDescriptionCondition(
                    search,
                    condition.value as string[],
                );
            case RuleConditionType.AMOUNT:
                return searchAmountCondition(
                    Number(search),
                    condition.value as RuleConditionAmount,
                );
            case RuleConditionType.FINANCIAL_ACCOUNTS:
                return searchFinancialAccountsCondition({
                    search,
                    financialAccountIds: condition.value as number[],
                    accounts: financialAccounts,
                });
            case RuleConditionType.ENTITY:
                return searchEntityCondition({
                    search,
                    entityIds: condition.value as number[],
                    entities,
                });
            default:
                return false;
        }
    });
}

function searchCounterpartyCondition({
    search,
    counterparties,
    counterpartyId,
}: {
    search: string;
    counterparties: Counterparty[];
    counterpartyId: string;
}) {
    return containsTextCaseInsensitive(
        findEntityById(counterparties, counterpartyId)?.name,
        search,
    );
}

function searchCategoryCondition({
    search,
    categoryId,
    categoryLabelGetter,
}: {
    search: string;
    categoryId: string;
    categoryLabelGetter: (id: string) => string;
}) {
    if (!categoryId) {
        return false;
    }
    return containsTextCaseInsensitive(categoryLabelGetter(categoryId), search);
}

function searchDescriptionCondition(search: string, descriptions?: string[]) {
    if (!descriptions) {
        return false;
    }
    return descriptions.some((description) =>
        containsTextCaseInsensitive(description, search),
    );
}

function searchAmountCondition(search: number, amount: RuleConditionAmount) {
    if (!amount || isNaN(search)) {
        return false;
    }
    return amount.min === search || amount.max === search;
}

function searchFinancialAccountsCondition({
    search,
    financialAccountIds,
    accounts,
}: {
    search: string;
    financialAccountIds: number[];
    accounts: FinancialAccount[];
}) {
    if (!financialAccountIds) {
        return false;
    }

    return financialAccountIds.some((id) =>
        containsTextCaseInsensitive(findEntityById(accounts, id)?.name, search),
    );
}

function searchEntityCondition({
    search,
    entityIds,
    entities,
}: {
    search: string;
    entityIds: number[];
    entities: Entity[];
}) {
    return entityIds.some((id) =>
        containsTextCaseInsensitive(findEntityById(entities, id)?.name, search),
    );
}

function containsTextCaseInsensitive(
    text: string | undefined | null,
    search: string,
): boolean {
    if (!text) {
        return false;
    }
    return text.toLowerCase().includes(search.toLowerCase());
}

export function findEntityById<T extends { id: string | number | null }>(
    entities: T[],
    id: string | number | null,
): T | undefined {
    if (!id) {
        return undefined;
    }
    return entities.find((entity) => entity.id === id);
}

export function getRuleTransactionsLink(rule: TransactionRule) {
    return `/transactions?filters=${JSON.stringify({ transactionRulesIds: [rule.id] })}`;
}
